import { useState } from 'react'
import { Button } from '@/components/ui/button'
import { Toast } from '@/components/ui/toast'
import { useAuthStore } from '@/lib/store'
import { supabase } from '@/lib/supabase'
import {
  X,
  DollarSign,
  FileText,
  AlertCircle,
  Info,
  Briefcase
} from 'lucide-react'

interface LoanRequestFormProps {
  onClose: () => void
  onSuccess: () => void
}

export function LoanRequestForm({ onClose, onSuccess }: LoanRequestFormProps) {
  const user = useAuthStore((state) => state.user)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [form, setForm] = useState({
    amount: '',
    purpose: '',
    businessPlan: ''
  })

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      if (!user) {
        throw new Error('Vous devez être connecté pour faire une demande de prêt')
      }

      // Validate amount
      const amount = parseFloat(form.amount)
      if (isNaN(amount) || amount <= 0) {
        throw new Error('Veuillez entrer un montant valide')
      }

      // Create loan request
      const { error } = await supabase
        .from('loan_requests')
        .insert([
          {
            seller_id: user.id,
            amount,
            purpose: form.purpose,
            business_plan: form.businessPlan || null,
            status: 'pending'
          }
        ])

      if (error) throw error

      // Create notification for admin
      const { error: notifError } = await supabase
        .from('notifications')
        .insert([
          {
            title: 'Nouvelle demande de prêt',
            content: `Un vendeur a soumis une demande de prêt de $${amount}`,
            type: 'system',
            metadata: {
              seller_id: user.id,
              seller_name: user.full_name || user.email,
              amount
            }
          }
        ])

      if (notifError) {
        console.error('Error creating notification:', notifError)
      }

      setToastMessage('Demande de prêt soumise avec succès')
      setToastType('success')
      setShowToast(true)
      
      // Wait a bit before closing the modal
      setTimeout(() => {
        onSuccess()
        onClose()
      }, 2000)
    } catch (err) {
      console.error('Error submitting loan request:', err)
      setToastMessage(err instanceof Error ? err.message : 'Erreur lors de la soumission de la demande de prêt')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
        />
      )}

      <div className="bg-white rounded-lg w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-4 sm:p-6">
          <div className="flex justify-between items-start border-b border-gray-200 pb-4">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900">
                Demande de Prêt
              </h2>
              <div className="mt-2 flex items-center text-sm text-blue-600 bg-blue-50 rounded-lg p-2">
                <Info className="h-4 w-4 mr-2" />
                Votre demande sera examinée par notre équipe financière
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="mt-6 space-y-6">
            {/* Loan Amount */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <DollarSign className="h-5 w-5 mr-2" />
                Montant du Prêt
              </h3>
              <div>
                <label htmlFor="amount" className="block text-sm font-medium text-gray-700">
                  Montant demandé (USD) *
                </label>
                <div className="mt-1 relative rounded-md shadow-sm">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <DollarSign className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    type="number"
                    id="amount"
                    required
                    min="100"
                    step="0.01"
                    value={form.amount}
                    onChange={(e) => setForm(prev => ({ ...prev, amount: e.target.value }))}
                    className="mt-1 block w-full pl-10 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm py-2"
                    placeholder="1000.00"
                  />
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Montant minimum: $100
                </p>
              </div>
            </div>

            {/* Loan Purpose */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Briefcase className="h-5 w-5 mr-2" />
                Objectif du Prêt
              </h3>
              <div>
                <label htmlFor="purpose" className="block text-sm font-medium text-gray-700">
                  Objectif du prêt *
                </label>
                <textarea
                  id="purpose"
                  required
                  rows={3}
                  value={form.purpose}
                  onChange={(e) => setForm(prev => ({ ...prev, purpose: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm px-4 py-2"
                  placeholder="Expliquez comment vous prévoyez d'utiliser ce prêt pour développer votre activité..."
                />
              </div>
            </div>

            {/* Business Plan */}
            <div className="bg-gray-50 rounded-lg p-4 sm:p-6 border border-gray-200">
              <h3 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <FileText className="h-5 w-5 mr-2" />
                Plan d'Affaires
              </h3>
              <div>
                <label htmlFor="businessPlan" className="block text-sm font-medium text-gray-700">
                  Plan d'affaires (optionnel)
                </label>
                <textarea
                  id="businessPlan"
                  rows={5}
                  value={form.businessPlan}
                  onChange={(e) => setForm(prev => ({ ...prev, businessPlan: e.target.value }))}
                  className="mt-1 block w-full border border-gray-300 rounded-lg shadow-sm focus:ring-2 focus:ring-primary focus:border-primary sm:text-sm px-4 py-2"
                  placeholder="Décrivez votre plan d'affaires, y compris les projections de revenus, les délais et la stratégie de remboursement..."
                />
                <p className="mt-1 text-xs text-gray-500">
                  Un plan d'affaires détaillé augmente vos chances d'approbation
                </p>
              </div>
            </div>

            {/* Terms and Conditions */}
            <div className="mt-6 bg-yellow-50 rounded-lg p-4">
              <h4 className="text-sm font-medium text-yellow-800 flex items-center">
                <AlertCircle className="h-5 w-5 mr-2" />
                Informations Importantes:
              </h4>
              <ul className="mt-2 text-sm text-yellow-700 list-disc list-inside space-y-1">
                <li>Les demandes de prêt sont examinées dans un délai de 3 à 5 jours ouvrables</li>
                <li>Des documents supplémentaires peuvent être demandés pour vérification</li>
                <li>Les taux d'intérêt varient en fonction de votre historique de vente et de votre ancienneté</li>
                <li>Le remboursement se fait par prélèvement automatique sur vos ventes futures</li>
              </ul>
            </div>

            <div className="pt-6 border-t flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
              <Button
                type="button"
                variant="secondary"
                onClick={onClose}
                disabled={loading}
                className="w-full sm:w-auto"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                variant="primary"
                disabled={loading}
                className="w-full sm:w-auto"
              >
                {loading ? (
                  <div className="w-6 h-6 border-2 border-white border-t-transparent rounded-full animate-spin" />
                ) : (
                  'Soumettre la demande'
                )}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}